import React from 'react'

import { ExportableIcon } from '../../../../ExportableIcon'

import { ReactComponent as FrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as TextLayerIcon } from '@sketch/icons/textformat-17'
import { ReactComponent as ShapeLayerIcon } from '@sketch/icons/squares-2-17'
import { ReactComponent as BitmapLayerIcon } from '@sketch/icons/image-17'
import { ReactComponent as LayerGroupIcon } from '@sketch/icons/folder-closed-17'
import { ReactComponent as SliceIcon } from '@sketch/icons/square-knife-16'
import { ReactComponent as SymbolIcon } from '@sketch/icons/symbol-17'
import { ReactComponent as GraphicIcon } from '@sketch/icons/img-frame-graphic-17'
import { ReactComponent as CanvasDottedIcon } from '@sketch/icons/canvas-dotted-18'

import * as S from './ElementIcon.styles'
import { ElementType } from '../../../../../../../inspector'
import { useTheme } from 'styled-components'

export interface ElementIconProps {
  elementType: ElementType
  className?: string
  hasExports: boolean
  isSymbol: boolean
}

export function ElementIcon({
  elementType,
  hasExports,
  isSymbol,
  className,
}: ElementIconProps) {
  const theme = useTheme()
  const { component: Component, ...props } = imagePropsForSelection(
    elementType,
    isSymbol
  )

  return (
    <S.ElementIcon className={className} $isSymbol={isSymbol}>
      {/* Slice already has an exportable icon present on the icon */}
      {hasExports && elementType !== 'slice' && (
        <S.ExportableIconWrapper elementType={elementType}>
          <ExportableIcon
            maskColor={
              isSymbol
                ? theme.colors.inspect.symbol.A
                : theme.colors.foreground.secondary.D
            }
          />
        </S.ExportableIconWrapper>
      )}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <S.Icon $isSymbol={isSymbol} as={Component} {...props} />
    </S.ElementIcon>
  )
}

const imagePropsForSelection = (
  elementType: ElementType,
  isSymbol: boolean
) => {
  if (isSymbol) {
    return {
      component: SymbolIcon,
      'aria-label': 'Symbol icon',
      role: 'img',
      width: '17px',
      height: '17px',
    }
  }

  switch (elementType) {
    case 'graphic':
      return {
        component: GraphicIcon,
        'aria-label': 'Graphic icon',
        role: 'img',
        width: '17px',
        height: '17px',
      }

    case 'text':
      return {
        component: TextLayerIcon,
        'aria-label': 'Text layer icon',
        role: 'img',
        width: '17px',
        height: '17px',
      }

    case 'bitmap':
      return {
        component: BitmapLayerIcon,
        'aria-label': 'Bitmap layer icon',
        role: 'img',
        width: '17px',
        height: '17px',
      }

    case 'group':
      return {
        component: LayerGroupIcon,
        'aria-label': 'Layer group icon',
        role: 'img',
        width: '17px',
        height: '17px',
      }

    case 'artboard':
      return {
        component: FrameIcon,
        'aria-label': 'Frame icon',
        role: 'img',
        width: '20px',
        height: '18px',
      }

    case 'slice':
      return {
        component: SliceIcon,
        'aria-label': 'Slice icon',
        role: 'img',
        width: '17px',
        height: '17px',
      }

    case 'hotspot': {
      return {
        // This was actually the page icon in the old inspector
        // which was used as default icon for unknown layers.
        // MSImmutableHotspotLayer did not have any icon so it was ending-up with this one.
        // We are just replicating the old behaviour here.
        component: CanvasDottedIcon,
        'aria-label': 'Hotspot icon',
        role: 'img',
        width: '18px',
        height: '18px',
      }
    }

    case 'polygon':
    case 'triangle':
    case 'star':
    case 'rectangle':
    case 'oval':
    case 'shapePath':
    case 'shapeGroup':
    default:
      return {
        component: ShapeLayerIcon,
        'aria-label': 'Shape layer icon',
        role: 'img',
        width: '17px',
        height: '17px',
      }
  }
}
